import React, { useMemo } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { type Edges, SafeAreaView } from 'react-native-safe-area-context';
import { theme, useResponsive } from '@sg/garnish';

import {
  OrderPlaybackCta,
  OrderPlaybackFadeInView,
  OrderPlaybackGoBack,
  OrderPlaybackLineItems,
  OrderPlaybackLocation,
  OrderPlaybackOrderDetails,
  OrderPlaybackTime,
  OrderPlaybackTitle,
} from './components';
import { getTime, useDate } from './utils';
import { type BagOrderPlaybackProps } from './utils';

/**
 * Order playback component for displaying order details.
 */
export const BagOrderPlayback = (props: BagOrderPlaybackProps) => {
  const {
    products,
    wantedTime = new Date(),
    isWantedTimeAsap,
    orderChannel,
    locationName,
    orderPlaybackCta,
    orderPlaybackConfig,
    isLoading,
    isPostOrder,
    goBack,
    confirmOrder,
  } = props;

  const {
    locationFadeIn,
    timeFadeIn,
    orderDetailsFadeIn,
    lineItemsFadeIn,
    confirmationFadeIn,
  } = orderPlaybackConfig;

  const date = useDate(wantedTime, isWantedTimeAsap);
  const time = getTime(wantedTime, orderChannel);
  const count = useMemo(() => {
    return products.reduce((acc, product) => acc + product.quantity, 0);
  }, [products]);

  const { minWidth, currentHeight } = useResponsive();
  const withSmallHeight = minWidth.isSM && currentHeight.isSM;

  return (
    <SafeAreaView
      edges={SAFE_AREA_EDGES}
      style={[styles.container, withSmallHeight && styles.containerSmallHeight]}
    >
      <View style={styles.titleContainer}>
        <OrderPlaybackTitle />

        <OrderPlaybackGoBack
          goBack={goBack}
          isDisabled={isLoading || isPostOrder}
        />
      </View>

      <OrderPlaybackFadeInView fadeIn={isLoading ? 0 : locationFadeIn}>
        <OrderPlaybackLocation
          orderChannel={orderChannel}
          locationName={locationName}
        />
      </OrderPlaybackFadeInView>

      <OrderPlaybackFadeInView fadeIn={isLoading ? 0 : timeFadeIn}>
        <OrderPlaybackTime
          orderChannel={orderChannel}
          date={date}
          time={time}
        />
      </OrderPlaybackFadeInView>

      <OrderPlaybackFadeInView fadeIn={isLoading ? 0 : orderDetailsFadeIn}>
        <OrderPlaybackOrderDetails count={count} />
      </OrderPlaybackFadeInView>

      <OrderPlaybackFadeInView
        style={styles.lineItems}
        fadeIn={isLoading ? 0 : lineItemsFadeIn}
      >
        <OrderPlaybackLineItems
          fadeIn={isLoading ? 0 : lineItemsFadeIn}
          products={products}
        />
      </OrderPlaybackFadeInView>

      <OrderPlaybackCta
        orderPlaybackCta={orderPlaybackCta}
        fadeIn={isLoading ? 0 : lineItemsFadeIn}
        animationDuration={confirmationFadeIn}
        isLoading={isLoading}
        isPostOrder={isPostOrder}
        onPress={confirmOrder}
      />
    </SafeAreaView>
  );
};

// ─── Constants ────────────────────────────────────────────────────────────────

const SAFE_AREA_EDGES: Edges = Platform.select({
  android: ['top'],
  default: [],
});

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    paddingTop: theme.spacing['10'],
    paddingBottom: theme.spacing['6'],
    paddingHorizontal: theme.spacing['4'],
    gap: theme.spacing['4'],
  },
  containerSmallHeight: {
    paddingTop: theme.spacing['4'],
    paddingBottom: theme.spacing['4'],
  },
  titleContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  lineItems: {
    flex: 1,
    gap: theme.spacing['4'],
  },
});
